import { editorTopicsAPI, TopicAssociations, TopicsInfoTopic } from 'api/editor/topics';
import React, { useState } from 'react';
import { alpha, Box, Button, CircularProgress, Grid, Link, Stack, Typography } from '@mui/material';
import IconMi from 'components/common/icon/IconMi';
import LightTooltip from 'components/common/tooltip/LightTooltip';
import { openInNewTab } from 'tools/tools';
import { dispatchCustomEvent } from 'tools/events';
import useBundleTranslation from 'i18n';
import isOwnerVisible from 'tools/owners';
import { UserAuth } from 'store/auth';
import { useAppSelector } from 'store/hooks';
import DatabaseIcon from 'components/common/icon/DatabaseIcon';
import { renderHtml, renderShowdown } from 'tools/safeHtml';
import GlossaryTermStatus from 'components/common/glossary-term-status/GlossaryTermStatus';

const entity2icon: { [key: string]: string } = {
    dashboard_element: 'elements-2',
    dataset: 'datasets',
};

export default function TopicItem({
    topicInfo,
    sectionIcon,
    showViewRelatedButton = true,
    viewRelatedButtonLabel,
    enableViewRelatedEvent = false,
}: {
    topicInfo: TopicsInfoTopic;
    sectionIcon: React.JSX.Element | null;
    showViewRelatedButton?: boolean;
    viewRelatedButtonLabel?: string;
    enableViewRelatedEvent?: boolean;
}) {
    const { t } = useBundleTranslation('components/glossary-term/glossary');
    const { configuration }: UserAuth = useAppSelector((state) => state.auth);

    const [topicAssociations, setTopicAssociations] = useState<TopicAssociations | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    const showOwners =
        (topicInfo.business_owner_dn || topicInfo.technical_owner_dn || topicInfo.data_steward_dn) &&
        (isOwnerVisible(configuration.owners.business_owner, 'glossary') ||
            isOwnerVisible(configuration.owners.technical_owner, 'glossary') ||
            isOwnerVisible(configuration.owners.data_steward, 'glossary'));

    const getTopicPluginLabel = (pluginName: string, amount: number): string => {
        if (pluginName.includes('Microsoft Power BI')) {
            pluginName = 'Microsoft Power BI';
        }

        let label = pluginName + ' Object';

        if (amount > 1) {
            label = label + 's';
        }

        return `${amount} ${label}`;
    };

    const getTooltip = () => {
        if (topicAssociations) {
            const entities = topicAssociations.entities;
            const showPlugins = topicAssociations.plugin && topicAssociations.plugin_name;

            if (!entities) {
                return null;
            }

            return (
                <Stack direction={'column'} spacing={1} sx={{ fontSize: '12px' }}>
                    {Object.keys(entities).map((entity) => (
                        <Stack
                            key={entity}
                            direction={'row'}
                            justifyContent={'start'}
                            alignItems={'center'}
                            spacing={1}
                        >
                            <IconMi icon={entity2icon[entity]} sx={{ fontSize: 16 }} />
                            <Box>{` ${entities[entity]} ${
                                entities[entity] > 1 ? t(entity) : t(entity + '_singular')
                            }`}</Box>
                        </Stack>
                    ))}
                    {showPlugins &&
                        Object.keys(topicAssociations.plugin).map((pluginId) => (
                            <Stack
                                key={'plugin'}
                                direction={'row'}
                                justifyContent={'start'}
                                alignItems={'center'}
                                spacing={1}
                            >
                                <DatabaseIcon name={topicAssociations.plugin_name[parseInt(pluginId)]} />
                                <Box>
                                    {getTopicPluginLabel(
                                        topicAssociations.plugin_name[parseInt(pluginId)],
                                        topicAssociations.plugin[parseInt(pluginId)],
                                    )}
                                </Box>
                            </Stack>
                        ))}
                </Stack>
            );
        }

        return (
            <Stack direction={'row'} spacing={1}>
                <CircularProgress size={'16px'} />
                <Typography>{t('loading')}</Typography>
            </Stack>
        );
    };

    const loadAssociations = () => {
        if (!isLoading && !topicAssociations) {
            setIsLoading(true);
            editorTopicsAPI.getTopicsAssociations(topicInfo.topic_id).then((response) => {
                if (response.status === 'OK' && response.data) {
                    setTopicAssociations(response.data.topicAssociations);
                    setIsLoading(false);
                }
            });
        }
    };

    const onButtonClick = () => {
        if (enableViewRelatedEvent) {
            dispatchCustomEvent('mi-glossary-term-popup', {
                action: 'view-related-click',
                payload: {
                    topicId: topicInfo.topic_id,
                    relatedElements: topicAssociations,
                },
            });
        } else {
            openInNewTab(`/search/global?tag_name=${topicInfo.name}`);
        }
    };

    return (
        <>
            <Stack direction="row" justifyContent="space-between" alignItems={'flex-start'} marginBottom={2}>
                <Stack direction="row" alignItems="flex-start" sx={{ minWidth: 0 }}>
                    {sectionIcon}
                    <Box
                        sx={{
                            ml: sectionIcon ? 1 : 0,
                            wordWrap: 'break-word',
                            overflow: 'hidden',
                            fontSize: '14px',
                            color: 'primary.main',
                            fontWeight: 600,
                            minWidth: 0,
                        }}
                    >
                        <Box component={'span'} sx={{ mr: 1 }}>{` ${topicInfo.name}`}</Box>
                        <GlossaryTermStatus
                            status={topicInfo?.status}
                            sx={{ verticalAlign: 'top', wordWrap: 'normal' }}
                        />
                    </Box>
                </Stack>
                {showViewRelatedButton && (
                    <LightTooltip
                        title={getTooltip()}
                        arrow={false}
                        placement={'bottom-end'}
                        sx={{ '.MuiTooltip-tooltip': { mt: '4px !important', p: 1, borderRadius: '3px' } }}
                    >
                        <Box display="flex" justifyContent="end" flexShrink={0}>
                            <Button
                                variant="text"
                                color={'primary'}
                                startIcon={<IconMi icon={'external-link'} />}
                                onClick={onButtonClick}
                                onMouseOver={loadAssociations}
                            >
                                {viewRelatedButtonLabel ?? t('view_other_items')}
                            </Button>
                        </Box>
                    </LightTooltip>
                )}
            </Stack>
            <Grid container direction={'row'} justifyContent={'space-between'} spacing={2}>
                <Grid item xs={8}>
                    <Stack
                        fontWeight={600}
                        fontSize={'12px'}
                        color="primary"
                        display="flex"
                        alignItems="center"
                        sx={{ mr: 4 }}
                    >
                        {topicInfo.definition ? (
                            <Stack width={1}>
                                <Stack direction="row" alignItems="center">
                                    <Box sx={{ mr: 1, color: 'primary.main', fontSize: '10px' }}>{t('definition')}</Box>
                                    <Box
                                        sx={{
                                            width: '100%',
                                            height: '1px',
                                            background: (theme) => alpha(theme.palette.primary.main, 0.24),
                                        }}
                                    ></Box>
                                </Stack>
                                <Box
                                    sx={{ mb: 2, mt: 0.5, color: 'text.primary', fontWeight: 400 }}
                                    className={'richtext-holder'}
                                >
                                    {renderHtml(topicInfo.definition ?? '')}
                                </Box>
                            </Stack>
                        ) : null}
                        {topicInfo.description ? (
                            <Stack width={1}>
                                <Stack direction="row" alignItems="center">
                                    <Box sx={{ mr: 1, color: 'primary.main', fontSize: '10px' }}>
                                        {t('description')}
                                    </Box>
                                    <Box
                                        sx={{
                                            width: '100%',
                                            height: '1px',
                                            background: (theme) => alpha(theme.palette.primary.main, 0.24),
                                        }}
                                    ></Box>
                                </Stack>
                                <Box
                                    sx={{ mb: 2, mt: 0.5, color: 'text.primary', fontWeight: 400 }}
                                    className={'richtext-holder'}
                                >
                                    {renderHtml(topicInfo.description ?? '')}
                                </Box>
                            </Stack>
                        ) : null}
                        {topicInfo.customFields[0]?.fields?.length ? (
                            <>
                                {topicInfo.customFields.map((customField) => {
                                    return (
                                        <Stack width={1}>
                                            <Stack direction="row" alignItems="center">
                                                <Box
                                                    sx={{
                                                        mr: 1,
                                                        color: 'primary.main',
                                                        fontSize: '10px',
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    {customField.label}
                                                </Box>
                                                <Box
                                                    sx={{
                                                        width: '100%',
                                                        height: '1px',
                                                        background: (theme) => alpha(theme.palette.primary.main, 0.24),
                                                    }}
                                                ></Box>
                                            </Stack>
                                            <Box sx={{ mb: 2, mt: 0.5, color: 'text.primary', fontWeight: 400 }}>
                                                {customField.fields.map((field) => {
                                                    return (
                                                        <Stack
                                                            width={1}
                                                            direction="row"
                                                            sx={{
                                                                padding: '8px 0',
                                                                '&:last-child': {
                                                                    paddingBottom: 0,
                                                                },
                                                                borderTop: (theme) =>
                                                                    `1px solid ${alpha(
                                                                        theme.palette.text.primary,
                                                                        0.08,
                                                                    )}}`,
                                                            }}
                                                        >
                                                            <Grid xs={6} direction="row" alignItems="center">
                                                                <Box
                                                                    sx={{
                                                                        mr: 1,
                                                                        fontSize: '12px',
                                                                        whiteSpace: 'nowrap',
                                                                    }}
                                                                >
                                                                    {field.name}
                                                                </Box>
                                                            </Grid>
                                                            <Grid xs={6}>
                                                                <Box
                                                                    sx={{
                                                                        fontSize: '12px',
                                                                        color: 'text.primary',
                                                                        fontWeight: 400,
                                                                        '& p': { m: 0 },
                                                                    }}
                                                                    dangerouslySetInnerHTML={{
                                                                        __html: renderShowdown(
                                                                            Object.values(field.values).join(', '),
                                                                        ),
                                                                    }}
                                                                ></Box>
                                                            </Grid>
                                                        </Stack>
                                                    );
                                                })}
                                            </Box>
                                        </Stack>
                                    );
                                })}
                            </>
                        ) : null}
                    </Stack>
                </Grid>
                <Grid item xs={4}>
                    {showOwners && (
                        <Stack
                            direction={'row'}
                            alignItems={'flex-start'}
                            spacing={1}
                            sx={{
                                padding: '12px',
                                background: (theme) => alpha(theme.palette.primary.main, 0.04),
                            }}
                        >
                            <Stack direction="column" width={1}>
                                {(() => {
                                    type OwnerKey = 'business_owner' | 'technical_owner' | 'data_steward';
                                    const owners = [
                                        {
                                            key: 'business_owner',
                                            label: configuration.owners.business_owner.label,
                                            name: topicInfo.business_owner_dn,
                                            email: topicInfo.business_owner_email,
                                            visible:
                                                topicInfo.business_owner_dn &&
                                                isOwnerVisible(configuration.owners.business_owner, 'glossary'),
                                        },
                                        {
                                            key: 'technical_owner',
                                            label: configuration.owners.technical_owner.label,
                                            name: topicInfo.technical_owner_dn,
                                            email: topicInfo.technical_owner_email,
                                            visible:
                                                topicInfo.technical_owner_dn &&
                                                isOwnerVisible(configuration.owners.technical_owner, 'glossary'),
                                        },
                                        {
                                            key: 'data_steward',
                                            label: configuration.owners.data_steward.label,
                                            name: topicInfo.data_steward_dn,
                                            email: topicInfo.data_steward_email,
                                            visible:
                                                topicInfo.data_steward_dn &&
                                                isOwnerVisible(configuration.owners.data_steward, 'glossary'),
                                        },
                                    ];

                                    const visibleOwners = owners
                                        .filter((o) => o.visible)
                                        .sort(
                                            (a, b) =>
                                                configuration.owners[a.key as OwnerKey].displayOrder -
                                                configuration.owners[b.key as OwnerKey].displayOrder,
                                        );

                                    return visibleOwners.map((owner, idx) => (
                                        <Stack
                                            key={owner.key}
                                            sx={{
                                                width: '100%',
                                                paddingTop: idx === 0 ? 0 : '12px',
                                                paddingBottom: '12px',
                                                borderTop:
                                                    idx === 0
                                                        ? 'none'
                                                        : (theme) =>
                                                              `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
                                            }}
                                        >
                                            {`${owner.label}: `}
                                            <Link
                                                href={`mailto:${owner.email}?subject=${topicInfo.name}`}
                                                sx={{
                                                    cursor: 'pointer',
                                                    textDecoration: 'none',
                                                    display: 'flex',
                                                    mt: 1,
                                                }}
                                            >
                                                <IconMi sx={{ mr: 1 }} fontSize={'16'} icon="user" />
                                                {owner.name}
                                            </Link>
                                        </Stack>
                                    ));
                                })()}
                            </Stack>
                        </Stack>
                    )}
                </Grid>
            </Grid>
        </>
    );
}
